import * as React from "react";
import { useAuth } from "../../hooks/auth";
import { AvatarWithUpload } from "../avatar";
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Notifications from "../notifications";
import RecentPages from "../recentPages";
import { getUrlFile } from "@/services";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";

export default function AccountMenu({
  open,
  variant = "sidebar",
  visibleItems = ["favorites", "notifications", "profile", "logout"],
}: {
  open?: boolean;
  variant?: "sidebar" | "header";
  visibleItems?: Array<"favorites" | "notifications" | "profile" | "logout">;
}) {
  const { logout, user } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const signOut = () => {
    logout();
    handleClose();
  };

  const theme = useTheme();

  return (
    <React.Fragment>
      {user?.id &&
        (variant === "header" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              ml: "auto",
            }}
          >
            {visibleItems.includes("favorites") && (
              <RecentPages
                renderTrigger={(onClick, ref, badgeContent) => (
                  <IconButton
                    ref={ref as React.RefObject<HTMLButtonElement>}
                    onClick={onClick}
                    size="small"
                  >
                    <Badge
                      badgeContent={badgeContent}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          minWidth: 14,
                          height: 14,
                          backgroundColor: "#ffa726",
                          color: "#fff",
                        },
                      }}
                    >
                      <StarBorderIcon sx={{ color: theme.palette.brand.accent[500] }} />
                    </Badge>
                  </IconButton>
                )}
              />
            )}
            {visibleItems.includes("notifications") && (
              <Notifications
                renderTrigger={(onClick, ref, badgeContent) => (
                  <IconButton
                    ref={ref as React.RefObject<HTMLButtonElement>}
                    onClick={onClick}
                    size="small"
                    sx={{ color: "#fbc02d" }}
                  >
                    <Badge
                      badgeContent={badgeContent}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          minWidth: 14,
                          height: 14,
                          backgroundColor: "#ff5722",
                          color: "#fff",
                        },
                      }}
                    >
                      <NotificationsNoneOutlinedIcon
                        sx={{
                          color: theme.palette.brand.accent[500],
                          animation:
                            badgeContent > 0
                              ? "swing 2s ease infinite"
                              : "none",
                          "@keyframes swing": {
                            "0%, 100%": { transform: "rotate(0deg)" },
                            "20%": { transform: "rotate(15deg)" },
                            "40%": { transform: "rotate(-10deg)" },
                            "60%": { transform: "rotate(5deg)" },
                            "80%": { transform: "rotate(-5deg)" },
                          },
                        }}
                      />
                    </Badge>
                  </IconButton>
                )}
              />
            )}
            {visibleItems.includes("profile") && (
              <>
                <Box
                  onClick={handleAvatarClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    borderRadius: "24px",
                    padding: "2px 4px",
                    transition: "background-color 0.2s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <AvatarWithUpload
                    alt={user?.name}
                    key={user?.idFilePhoto}
                    src={getUrlFile(user?.idFilePhoto, "small")}
                    sx={{
                      width: 30,
                      height: 30,
                    }}
                  />
                </Box>
                <Menu
                  anchorEl={anchorEl}
                  id="account-menu"
                  open={openMenu}
                  onClose={handleClose}
                  onClick={handleClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: "visible",
                      filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                      mt: 1.5,
                      "&:before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {user.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                  {visibleItems.includes("logout") && (
                    <Box>
                      <Divider />
                      <MenuItem
                        onClick={signOut}
                        sx={{ color: "error.main", mt: 1 }}
                      >
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        Sair
                      </MenuItem>
                    </Box>
                  )}
                </Menu>
              </>
            )}
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ width: "100%", pb: open ? 2 : 0 }}
          >
            {open ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 1,
                }}
              >
                {/* User Profile Section */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    gap: 1.5,
                  }}
                >
                  <AvatarWithUpload
                    alt={user?.name}
                    key={user?.idFilePhoto}
                    src={getUrlFile(user?.idFilePhoto, "small")}
                    sx={{
                      width: 40,
                      height: 40,
                    }}
                  />
                  {visibleItems.includes("profile") && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        noWrap
                        title={user.name}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.contrastText,
                          lineHeight: 1.2,
                          fontSize: "0.85rem",
                        }}
                      >
                        {user.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        title={user.email}
                        sx={{
                          color: "#757575",
                          lineHeight: 1.2,
                          fontSize: "0.75rem",
                        }}
                      >
                        {user.email}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", ml: "auto", gap: 0.5 }}>
                    <RecentPages
                      renderTrigger={(onClick, ref, badgeContent) => (
                        <IconButton
                          ref={ref as React.RefObject<HTMLButtonElement>}
                          onClick={onClick}
                          size="small"
                          sx={{ color: "#757575", padding: "4px" }}
                        >
                          <Badge
                            badgeContent={badgeContent}
                            sx={{
                              "& .MuiBadge-badge": {
                                fontSize: "0.6rem",
                                fontWeight: 600,
                                minWidth: 14,
                                height: 14,
                                backgroundColor: "#ffa726",
                                color: "#fff",
                              },
                            }}
                          >
                            <StarBorderIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      )}
                    />
                    <Notifications
                      renderTrigger={(onClick, ref, badgeContent) => (
                        <IconButton
                          ref={ref as React.RefObject<HTMLButtonElement>}
                          onClick={onClick}
                          size="small"
                          sx={{ color: theme.palette.brand.accent[500], padding: "4px" }}
                        >
                          <Badge
                            badgeContent={badgeContent}
                            sx={{
                              "& .MuiBadge-badge": {
                                fontSize: "0.6rem",
                                fontWeight: 600,
                                minWidth: 14,
                                height: 14,
                                backgroundColor: "#ff5722",
                                color: "#fff",
                              },
                            }}
                          >
                            <NotificationsNoneOutlinedIcon
                              fontSize="small"
                              sx={{
                                color: theme.palette.brand.accent[500],
                                animation:
                                  badgeContent > 0
                                    ? "swing 2s ease infinite"
                                    : "none",
                                "@keyframes swing": {
                                  "0%, 100%": { transform: "rotate(0deg)" },
                                  "20%": { transform: "rotate(15deg)" },
                                  "40%": { transform: "rotate(-10deg)" },
                                  "60%": { transform: "rotate(5deg)" },
                                  "80%": { transform: "rotate(-5deg)" },
                                },
                              }}
                            />
                          </Badge>
                        </IconButton>
                      )}
                    />
                  </Box>
                </Box>

                {/* Actions List */}
                <Box sx={{ px: 2 }}>
                  <ListItemButton
                    onClick={signOut}
                    sx={{
                      borderRadius: "8px",
                      py: 0.5,
                      mt: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: "#D32F2F" }}>
                      <LogoutIcon
                        sx={{ fontSize: "1.1rem", color: "#FF0000" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Sair"
                      primaryTypographyProps={{
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        color: "#D32F2F",
                      }}
                    />
                  </ListItemButton>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "0 12px",
                  gap: 1,
                  py: 2,
                }}
              >
                <RecentPages
                  renderTrigger={(onClick, ref, badgeContent) => (
                    <IconButton
                      ref={ref as React.RefObject<HTMLButtonElement>}
                      onClick={onClick}
                      size="small"
                      sx={{ color: "#757575" }}
                    >
                      <Badge
                        badgeContent={badgeContent}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            minWidth: 14,
                            height: 14,
                            backgroundColor: "#ffa726",
                            color: "#fff",
                          },
                        }}
                      >
                        <StarBorderIcon fontSize="small" />
                      </Badge>
                    </IconButton>
                  )}
                />
                <Notifications
                  renderTrigger={(onClick, ref, badgeContent) => (
                    <IconButton
                      ref={ref as React.RefObject<HTMLButtonElement>}
                      onClick={onClick}
                      size="small"
                      sx={{ color: theme.palette.brand.accent[500] }}
                    >
                      <Badge
                        badgeContent={badgeContent}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            minWidth: 20,
                            height: 20,
                            backgroundColor: "#ff5722",
                            color: "#fff",
                          },
                        }}
                      >
                        <NotificationsNoneOutlinedIcon
                          fontSize="small"
                          sx={{ color: theme.palette.brand.accent[500] }}
                        />
                      </Badge>
                    </IconButton>
                  )}
                />
                <IconButton
                  onClick={signOut}
                  size="small"
                  sx={{
                    color: "#757575",
                    "&:hover": { color: "#D32F2F" },
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
                {visibleItems.includes("profile") && (
                  <IconButton onClick={handleAvatarClick} sx={{ p: 0, mt: 1 }}>
                    <Avatar
                      alt={user?.name}
                      src={getUrlFile(user?.idFilePhoto, "small")}
                      sx={{ width: 36, height: 36 }}
                    />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        ))}
    </React.Fragment>
  );
}
